import React from "react";
import { useParams } from "react-router-dom";
import {
    useGetCompanyByIdQuery,
} from "../Service/companiesApi";

import { Company } from "../Types/companyTypes";
import { companyValues } from "../initialValues";
import { companyFields } from "../fields";
import { CompaniesRoutes } from "../../../Router/path";
import { CircularProgress, Typography } from "@mui/material";
import EntityFormLayout from "../../Shared/Components/EntityFormLayout/EntityFormLayout";
import CompanyAccountsBankTable from "../Componets/CompanyAccountsBankTable";
import { useGetBankAccountsForCompanyQuery } from "../../BankAccounts";
import {
  useUpdateBankAccountExtMutation,
  useGetCompanyImagesQuery,
  useUploadCompanyImagesMutation,
  useDeleteCompanyImageMutation,
} from '../Service/extendedCompaniesApi';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import ImageIcon from '@mui/icons-material/Image';
import UploadImagesForm from '../../Shared/Components/UploadImagesForm/UploadImagesForm';
import UploadedImagesGallery from '../../Shared/Components/UploadedImagesGallery/UploadedImagesGallery';

const EditCompany = () => {
  const companyId = useParams<{ companyId: string }>().companyId;
  const parsedCompanyId = companyId ? parseInt(companyId) : 0;

  const {
    data: companyDataById,
    error: companyDataByIdError,
    isLoading: companyDataByIdLoading,
  } = useGetCompanyByIdQuery(parsedCompanyId, { skip: !companyId });

  const {
    data: bankAccounts,
    isLoading: bankAccountsLoading,
    isError: bankAccountsError,
    error: bankAccountsFetchError,
    refetch: bankAccountsRefetch,
  } = useGetBankAccountsForCompanyQuery(parsedCompanyId, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: companyImages,
    isLoading: imagesLoading,
    refetch: refetchImages,
  } = useGetCompanyImagesQuery(parsedCompanyId);

  const [uploadCompanyImages, uploadState] = useUploadCompanyImagesMutation();

  const [updateCompany, statusUpdate] = useUpdateBankAccountExtMutation();

  const entityForm = useEntityForm<Company>({
    initialData: companyDataById ?? companyValues,
    fields: companyFields,
    submitFn: async (data) => {
      await updateCompany({ id: parsedCompanyId, data }).unwrap();

      if (typeof bankAccountsRefetch === 'function') {
        bankAccountsRefetch();
      } else {
        console.warn('bankAccountsRefetch nie jest funkcją!');
      }
    },
    mutationStatus: statusUpdate,
    redirectUrl: CompaniesRoutes.root,
  });
  if (companyDataByIdLoading) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Typography>
    );
  }

  if (companyDataByIdError) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        Nie udało się załadować danych firmy.
      </Typography>
    );
  }

  return (
    <EntityFormLayout {...entityForm}>
      <AccordionCard title="Logo" icon={<ImageIcon />}>
        {companyImages?.length ? (
          <>
            <UploadedImagesGallery
              images={companyImages}
              isLoading={imagesLoading}
              isError={false}
              useDeleteMutation={useDeleteCompanyImageMutation}
              mutationParams={(imageId: string) => ({ imageId, companyId: parsedCompanyId })}
              onDeleteSuccess={refetchImages}
            />
            <UploadImagesForm
              useUploadMutation={() => [uploadCompanyImages, uploadState]}
              mutationParams={(files) => ({ companyId: parsedCompanyId, files })}
              onUploadSuccess={refetchImages}
            />
          </>
        ) : (
          <Typography color="text.secondary" component="div">
            Nie wgrano jeszcze logo firmy.
            <UploadImagesForm
              useUploadMutation={() => [uploadCompanyImages, uploadState]}
              mutationParams={(files) => ({ companyId: parsedCompanyId, files })}
              onUploadSuccess={refetchImages}
            />
          </Typography>
        )}
      </AccordionCard>
      <CompanyAccountsBankTable
        companyId={parsedCompanyId}
        data={bankAccounts ?? []}
        isLoading={bankAccountsLoading}
        isError={bankAccountsError}
        error={bankAccountsFetchError}
      />
    </EntityFormLayout>
  );
};

export default EditCompany;
